import { useState } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useRolePermissions, useScrollSync } from '@/hooks';

import { usePriceProcessing } from '../../hooks/usePriceProcessing';
import { LoadingTable } from '../../components/';

import {
  FiltersSection,
  GlobalPriceTable,
  StationPriceTable,
  SaveConfirmDialog,
  GlobalPriceTableHeader,
  StationPriceTableHeader
} from './components';

import './PriceProcessingPage.css';
import './components/GlobalPriceTable/GlobalPriceTable.css';
import './components/FiltersSection/FiltersSection.css';
import './components/SaveConfirmDialog/SaveConfirmDialog.css';

/**
 * Main page component for price processing functionality
 * Manages price changes for global and station-level data with order and notification options
 *
 * Features:
 * - Hierarchical filtering by region, province, and brand
 * - Global price summary table with regional aggregation
 * - Station-specific price table with individual station data
 * - Save and process confirmation dialogs with notification settings
 * - Order creation and multi-channel notification support (SMS, Email, 1C, OKKO Core)
 * - Real-time change tracking and validation
 * - Responsive design for mobile and desktop
 
 */
function PriceProcessingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { canProcessOkkoPrices } = useRolePermissions();
  const { headerRef: globalHeaderRef, bodyRef: globalBodyRef } = useScrollSync();
  const { headerRef: stationsHeaderRef, bodyRef: stationsBodyRef } = useScrollSync();
  const [isExpanded, setIsExpanded] = useState(true);
  const isCollapsible = isMobile;

  const {
    regionalOffices,
    provinces,
    brands,
    fuelTypes,
    filters,
    activeFilters,
    globalPriceData,
    stationPriceData,
    hasChanges,
    isFiltersLoading,
    isFiltersFetching,
    hasPriceChanges,
    saveConfirmDialogOpen,
    createOrder,
    notifications,
    notificationDateTime,
    isLoading,
    resetKey,
    isCellChanged,
    hasOkkoChanges,
    setNotificationDateTime,
    setSaveConfirmDialogOpen,
    setNotifications,
    resetDialogState,

    onFilterChange,
    removeActiveFilter,
    onCreateOrderChange,
    onNotificationChange,
    handlePriceInput,

    onSave,
    onConfirmSave,
    onResetChanges,

    onDateChange
  } = usePriceProcessing();

  const handleCloseConfirmDialog = () => {
    setSaveConfirmDialogOpen(false);
    resetDialogState();
  };

  return (
    <div className={`price-processing-container ${isMobile ? 'mobile' : ''}`}>
      {isFiltersLoading ? (
        <LoadingTable message="Завантаження цін..." rows={10} columns={12} />
      ) : (
        <>
          {/* Sticky Header */}
          <div className={`price-processing-sticky-header ${isMobile ? 'mobile' : ''}`}>
            <div
              className={`price-processing-collapsible${isCollapsible ? (isExpanded ? ' expanded' : ' collapsed') : ''}`}
            >
              <FiltersSection
                isMobile={isMobile}
                regionalOffices={regionalOffices}
                provinces={provinces}
                brands={brands}
                filters={filters}
                activeFilters={activeFilters}
                hasPriceChanges={hasPriceChanges}
                hasChanges={hasChanges}
                isFiltersLoading={isFiltersFetching}
                onFilterChange={onFilterChange}
                removeActiveFilter={removeActiveFilter}
                onResetChanges={onResetChanges}
                onSave={onSave}
              />
            </div>

            {isCollapsible && (
              <button className="price-processing-toggle-bar" onClick={() => setIsExpanded(v => !v)}>
                <span className="price-processing-toggle-summary">
                  {!isExpanded && filters.regional_office
                    ? filters.regional_office
                    : isExpanded
                      ? 'Згорнути фільтри'
                      : 'Розгорнути фільтри'}
                </span>
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </button>
            )}

            {/* Global Price Changes Table */}
            {globalPriceData.length > 0 && fuelTypes.length > 0 && (
              <div className="price-processing-summary-container">
                <h2 className="price-processing-table-title">Зміна цін по регіонам</h2>

                <GlobalPriceTableHeader fuelTypes={fuelTypes} headerRef={globalHeaderRef} />
                <GlobalPriceTable
                  bodyRef={globalBodyRef}
                  data={globalPriceData}
                  fuelTypes={fuelTypes}
                  isMobile={isMobile}
                  canProcessOkkoPrices={canProcessOkkoPrices}
                  isCellChanged={isCellChanged}
                  handlePriceInput={handlePriceInput}
                  onDateChange={onDateChange}
                  resetKey={resetKey}
                />
              </div>
            )}

            {/* Station Details Table Header */}
            {stationPriceData.length > 0 && (
              <div className="price-processing-stations-header">
                <h2 className="price-processing-table-title">Зміна цін по конкретних АЗС</h2>
                <StationPriceTableHeader headerRef={stationsHeaderRef} fuelTypes={fuelTypes} />
              </div>
            )}
          </div>

          {/* Station-Specific Price Changes Table Body */}
          {stationPriceData.length > 0 && fuelTypes.length > 0 && (
            <StationPriceTable
              data={stationPriceData}
              fuelTypes={fuelTypes}
              isMobile={isMobile}
              canProcessOkkoPrices={canProcessOkkoPrices}
              isCellChanged={isCellChanged}
              handlePriceInput={handlePriceInput}
              onDateChange={onDateChange}
              bodyRef={stationsBodyRef}
              resetKey={resetKey}
            />
          )}
        </>
      )}
      {/* Save Confirmation Dialog */}
      <SaveConfirmDialog
        open={saveConfirmDialogOpen}
        createOrder={createOrder}
        notifications={notifications}
        notificationDateTime={notificationDateTime}
        isLoading={isLoading}
        setNotifications={setNotifications}
        hasOkkoChanges={hasOkkoChanges}
        onClose={handleCloseConfirmDialog}
        onConfirm={onConfirmSave}
        onCreateOrderChange={onCreateOrderChange}
        onNotificationChange={onNotificationChange}
        setNotificationDateTime={setNotificationDateTime}
      />
    </div>
  );
}

export default PriceProcessingPage;
